/**
 * The program monitor's pure geometry (1.7.0 stream B, docs/dev/formats-1.7.0-build.md): the CSS of a keyed or zoomed
 * crop on a media element, the wipe inset in the element's own coordinates, where a source picture sits in a frame,
 * a format's crop window mapped through the primary's own crop (the Guides outlines), the canvas rectangles Side by side
 * draws from the shared video elements, the Side by side panel sizes, and the drag, wheel and corner maths that turn a
 * gesture into pan and zoom values. No DOM here, so all of it is unit tested (apps/web/test/monitor-170.test.ts).
 */
import { ZOOM_MAX, ZOOM_MIN, clampReframe, type CropWindow, type Size } from '@prism/contracts';

/** A rectangle as fractions of a frame (or of a source picture): left, top, width, height. */
export interface FracRect { x: number; y: number; w: number; h: number }

const n6 = (v: number) => Number(v.toFixed(6));

/**
 * The transform that shows a crop window of the element's picture filling the element (object-fit: fill): the picture
 * is moved so the window's corner is at the origin, then scaled up by 1/w, 1/h (transform-origin 0 0). `pre` goes first
 * in the list (a slide's translateX, in the element's own size, which is the frame's).
 */
export function cropTransform(win: CropWindow, pre = ''): string {
  const t = `scale(${n6(1 / win.w)}, ${n6(1 / win.h)}) translate(${n6(-win.x * 100)}%, ${n6(-win.y * 100)}%)`;
  return pre ? `${pre} ${t}` : t;
}

/**
 * The clip-path that keeps only the crop window of an element (in the element's own coordinates, before its transform),
 * so a cropped layer does not spill out of its box. `wipe` (0…1 of the transition, 1 = fully in) also hides the left part
 * of the window that the incoming clip of a wipe has not reached yet. Without a window: the wipe on the whole element.
 */
export function cropClipPath(win: CropWindow | null, wipe?: number): string {
  const w = win ?? { x: 0, y: 0, w: 1, h: 1 };
  const shown = wipe === undefined ? 1 : Math.max(0, Math.min(1, wipe));
  const left = w.x + (1 - shown) * w.w;
  const pct = (v: number) => `${n6(Math.max(0, v) * 100)}%`;
  return `inset(${pct(w.y)} ${pct(1 - w.x - w.w)} ${pct(1 - w.y - w.h)} ${pct(left)})`;
}

/** Fit (contain) rectangle of a source shape in a frame shape, as fractions of the frame. */
export function containFrac(sourceAspect: number, frameAspect: number): FracRect {
  if (!(sourceAspect > 0) || !(frameAspect > 0)) return { x: 0, y: 0, w: 1, h: 1 };
  if (sourceAspect >= frameAspect) { const h = frameAspect / sourceAspect; return { x: 0, y: (1 - h) / 2, w: 1, h }; }
  const w = sourceAspect / frameAspect;
  return { x: (1 - w) / 2, y: 0, w, h: 1 };
}

/**
 * Where the whole source picture sits in a frame, as fractions of the frame: for a crop window (Fill), the picture
 * enlarged so the window covers the frame; without one (Fit, Blur), the contained picture.
 */
export function sourceInFrame(win: CropWindow | null, sourceAspect: number, frameAspect: number): FracRect {
  if (!win) return containFrac(sourceAspect, frameAspect);
  return { x: -win.x / win.w, y: -win.y / win.h, w: 1 / win.w, h: 1 / win.h };
}

/**
 * A format's crop window (fractions of the source) as a rectangle of the primary frame (Guides): mapped through where
 * the primary shows the source (its own crop window, or its Fit rectangle). Parts may lie outside 0…1: those are
 * outside the primary picture (drawn dashed).
 */
export function outlineInPrimary(win: CropWindow | null, primaryPlace: FracRect): FracRect {
  const w = win ?? { x: 0, y: 0, w: 1, h: 1 };
  return { x: primaryPlace.x + w.x * primaryPlace.w, y: primaryPlace.y + w.y * primaryPlace.h, w: w.w * primaryPlace.w, h: w.h * primaryPlace.h };
}

/** Whether a rectangle (fractions of the frame) reaches outside the frame. */
export const outsideFrame = (r: FracRect, eps = 1e-4): boolean => r.x < -eps || r.y < -eps || r.x + r.w > 1 + eps || r.y + r.h > 1 + eps;

/** The part of a rectangle inside the frame (null when none). */
export function insideFrame(r: FracRect): FracRect | null {
  const x0 = Math.max(0, r.x), y0 = Math.max(0, r.y), x1 = Math.min(1, r.x + r.w), y1 = Math.min(1, r.y + r.h);
  return x1 > x0 && y1 > y0 ? { x: x0, y: y0, w: x1 - x0, h: y1 - y0 } : null;
}

/**
 * The rectangle of a frame a Fit-shaped box covers, for a frame of another shape: the largest box of `shape`'s aspect
 * inside `frame`, centred (as fractions of the frame). The fallback outline when there is no clip under the playhead.
 */
export function centredBox(shapeAspect: number, frameAspect: number): FracRect {
  return containFrac(shapeAspect, frameAspect);
}

/** A canvas drawImage call: the source rectangle (source pixels) and the destination rectangle (canvas pixels). */
export interface DrawRect { sx: number; sy: number; sw: number; sh: number; dx: number; dy: number; dw: number; dh: number }

/**
 * What Side by side draws of a source into a box of a canvas: the crop window (Fill) over the whole box, or the whole
 * picture contained in it (Fit and Blur). `box` is in canvas pixels; the source in its own pixels.
 */
export function canvasDraw(win: CropWindow | null, source: Size, box: { x: number; y: number; width: number; height: number }): DrawRect {
  if (win) {
    return { sx: win.x * source.width, sy: win.y * source.height, sw: win.w * source.width, sh: win.h * source.height, dx: box.x, dy: box.y, dw: box.width, dh: box.height };
  }
  const r = containFrac(source.width / source.height, box.width / box.height);
  return { sx: 0, sy: 0, sw: source.width, sh: source.height, dx: box.x + r.x * box.width, dy: box.y + r.y * box.height, dw: r.w * box.width, dh: r.h * box.height };
}

/** The centre crop that fills a box with a source (a Blur fit's background), in source pixels. */
export function coverSource(source: Size, boxAspect: number): { sx: number; sy: number; sw: number; sh: number } {
  const sa = source.width / source.height;
  if (sa > boxAspect) { const sw = source.height * boxAspect; return { sx: (source.width - sw) / 2, sy: 0, sw, sh: source.height }; }
  const sh = source.width / boxAspect;
  return { sx: 0, sy: (source.height - sh) / 2, sw: source.width, sh };
}

/**
 * Side by side: every format at one shared height, next to each other, as large as fits `area` with `gap` pixels
 * between them. Returns each panel's size (whole pixels).
 */
export function sideBySideSizes(formats: Size[], area: Size, gap = 8): Size[] {
  if (!formats.length || !(area.width > 0) || !(area.height > 0)) return formats.map(() => ({ width: 0, height: 0 }));
  const sumAspect = formats.reduce((s, f) => s + f.width / f.height, 0);
  const free = Math.max(0, area.width - gap * (formats.length - 1));
  const h = Math.max(0, Math.min(area.height, free / sumAspect));
  return formats.map((f) => ({ width: Math.floor((h * f.width) / f.height), height: Math.floor(h) }));
}

/* ---------------------------------------------------------------- gestures → pan and zoom */

/** The pan value (−1…1) that puts a crop window of size `size` (fractions) at `pos`. 0 when the window has no room to move. */
export function panFromPos(pos: number, size: number): number {
  const spare = 1 - size;
  if (spare <= 1e-9) return 0;
  return clampReframe((2 * pos) / spare - 1);
}

/** The window position of a pan value. */
export const posFromPan = (pan: number, size: number): number => ((1 - size) * (1 + clampReframe(pan))) / 2;

/**
 * Dragging the picture (single view) by (dx, dy) fractions of the frame: the picture follows the pointer, so the crop
 * window moves the other way by dx · w of the source. Returns the new pan values.
 */
export function panByPictureDrag(win: CropWindow, dx: number, dy: number): { x: number; y: number } {
  return { x: panFromPos(win.x - dx * win.w, win.w), y: panFromPos(win.y - dy * win.h, win.h) };
}

/**
 * Dragging a Guides outline by (dx, dy) fractions of the primary frame: the window follows the pointer, through the
 * primary's placement of the source (`place`, fractions of the frame per source). Returns the new pan values.
 */
export function panByOutlineDrag(win: CropWindow, place: FracRect, dx: number, dy: number): { x: number; y: number } {
  return { x: panFromPos(win.x + dx / place.w, win.w), y: panFromPos(win.y + dy / place.h, win.h) };
}

export const clampZoomValue = (z: number): number => (Number.isFinite(z) ? Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, z)) : ZOOM_MIN);

/** The zoom after a wheel step (deltaY in pixels; a pinch on a trackpad comes as a wheel with ctrlKey, finer steps). */
export function zoomByWheel(zoom: number, deltaY: number, pinch = false): number {
  const k = pinch ? 0.01 : 0.0015;
  return n6(clampZoomValue(zoom * Math.exp(-deltaY * k)));
}

/** The zoom after a two-finger pinch from distance d0 to d1. */
export const zoomByPinch = (zoom: number, d0: number, d1: number): number => (d0 > 0 && d1 > 0 ? n6(clampZoomValue((zoom * d1) / d0)) : zoom);

/**
 * Dragging a Guides outline's corner: the outline grows or shrinks about its centre with the corner's distance from it,
 * so the zoom goes the other way (a larger window is less zoom).
 */
export const zoomByCorner = (zoom: number, d0: number, d1: number): number => (d0 > 0 && d1 > 0 ? n6(clampZoomValue((zoom * d0) / d1)) : zoom);

/**
 * The pan that keeps the window's centre where it was when the zoom changes (zooming about the middle of the picture).
 * `before` is the window before; w1, h1 its size after.
 */
export function panKeepingCentre(before: CropWindow, w1: number, h1: number): { x: number; y: number } {
  const cx = before.x + before.w / 2, cy = before.y + before.h / 2;
  return { x: panFromPos(cx - w1 / 2, w1), y: panFromPos(cy - h1 / 2, h1) };
}

/** The crop window size at a zoom for a source and frame shape (cropAt's w, h). */
export function windowSize(sourceAspect: number, frameAspect: number, zoom: number): { w: number; h: number } {
  const z = clampZoomValue(zoom);
  return { w: Math.min(1, frameAspect / sourceAspect) / z, h: Math.min(1, sourceAspect / frameAspect) / z };
}
